import { useMemo } from "react";
import { BookingService } from "@/services/BookingService";
import { useQuery } from "@tanstack/react-query";
import type { BookingFilters, BookingSort, Booking } from "@/types/Booking";
import type { PaginatedResponse } from "@/types/Pagination";

interface UseOrganisationBookingsQueryOptions {
  organisationId: string;
  filters?: Omit<BookingFilters, "organisationId">;
  sort?: BookingSort;
  skip?: number;
  limit?: number;
  enabled?: boolean;
  staleTime?: number;
}

/**
 * React Query hook that fetches a paginated page of bookings across an entire organisation.
 * The server expands `organisationId` to the union of every member currently in the org and
 * applies the rest of the filters / pagination.
 *
 * Authorization: elevated users (admin/PM/superadmin) and organisation owners may call this.
 * The server's BookingRepository expands organisationId to the org-member union for both groups.
 */
export function useOrganisationBookingsQuery(options: UseOrganisationBookingsQueryOptions) {
  const {
    organisationId,
    filters,
    sort,
    skip = 0,
    limit = 500,
    enabled = true,
    staleTime = 5 * 60 * 1000,
  } = options;

  const bookingService = useMemo(() => new BookingService(), []);

  return useQuery<PaginatedResponse<Booking>>({
    queryKey: ["bookings", "by-organisation", organisationId, filters, sort, skip, limit],
    queryFn: () =>
      bookingService.getAllBookings(
        false,
        { ...(filters ?? {}), organisationId },
        sort,
        skip,
        limit,
      ),
    enabled: enabled && !!organisationId,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}
